import { useState } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, List, ListItem, ListItemText, Alert, CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { enableConfigRecorders, disableConfigRecorders } from '../../../services/securityhubApi';
import { CK, FONT_BODY, insetPanelSx, dialogProps, dialogTitleSx } from '../portalStyles';

// Copy + action per mode. enable → minimal recording; disable → stop (keep recorder).
const COPY = {
  enable: {
    title: 'AWS Config needed for full evaluation',
    body: (n) => `AWS Config isn't recording in ${n} account/region pair(s). Enable minimal recording so your standards can fully evaluate.`,
    primary: 'Enable AWS Config',
    started: 'Enabling AWS Config — track progress on the AWS Config page.',
    run: (cells) => enableConfigRecorders(cells, 'minimal'),
  },
  disable: {
    title: 'Stop AWS Config recording?',
    body: (n) => `${n} account/region pair(s) are no longer used by any standard. Stop recording to avoid AWS Config charges? The recorder is kept — you can delete it later from the AWS Config page.`,
    primary: 'Stop recording',
    started: 'Stopping AWS Config recording — track progress on the AWS Config page.',
    run: (cells) => disableConfigRecorders(cells),
  },
};

export default function ConfigSetupDialog({ open, mode, cells, onClose, onDone }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [started, setStarted] = useState(false);

  if (!open || !mode || !cells || cells.length === 0) return null;
  const copy = COPY[mode];

  const handleClose = () => {
    setStarted(false);
    setError(null);
    if (onClose) onClose();
  };

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await copy.run(cells);
      setStarted(true);
      if (onDone) onDone();
      setTimeout(handleClose, 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : handleClose} {...dialogProps}>
      <DialogTitle sx={dialogTitleSx}>{copy.title}</DialogTitle>
      {/* MUI zeroes a DialogContent's top padding when it follows a DialogTitle
          (`.MuiDialogTitle-root + &`), which out-specifies a plain `pt` in sx —
          the self-scoped selector below restores it under the title band. */}
      <DialogContent sx={{ p: 3, '&.MuiDialogContent-root': { pt: 3 } }}>
        {started ? (
          <Alert severity="success" sx={{ borderRadius: '8px' }}>{copy.started}</Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 1.5, fontFamily: FONT_BODY, color: CK.body }}>
              {copy.body(cells.length)}
            </Typography>
            <Box sx={{ ...insetPanelSx, py: 1 }}>
              <List dense disablePadding>
                {cells.map((c) => (
                  <ListItem key={`${c.accountId}|${c.region}`} disableGutters sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={`${c.accountId} — ${c.region}`}
                      primaryTypographyProps={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.875rem',
                        color: CK.navy,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: '8px' }}>{error}</Alert>}
          </>
        )}
      </DialogContent>
      {!started && (
        <DialogActions sx={{ px: 3, pb: 2, pt: 0, flexWrap: 'wrap', gap: 1, '& > :not(:first-of-type)': { ml: 0 } }}>
          <Button onClick={() => navigate('/securityhub/config')} disabled={busy}>
            Open AWS Config page
          </Button>
          <Button onClick={handleClose} disabled={busy}>Later</Button>
          <Button variant="contained" onClick={run} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : null}>
            {copy.primary}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
